import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Plus, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExitSurveys() {
  const [surveys, setSurveys] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [form, setForm] = useState({ employee_id: '', employee_name: '', overall_rating: 3, reason_for_leaving: '', what_liked: '', what_could_improve: '', would_recommend: false, additional_comments: '' });
  const token = localStorage.getItem('token');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isManagement = ['admin', 'team_leader'].includes(user.role);

  const fetch = useCallback(async () => {
    try { const res = await axios.get(`${API}/exit/surveys`, { headers: getHeaders() }); setSurveys(res.data); } catch (e) { toast.error('Failed'); }
  }, []);
  const fetchUsers = useCallback(async () => {
    try { const res = await axios.get(`${API}/users`, { headers: getHeaders() }); setAllUsers(res.data); } catch (err) { console.error("./pages/ExitSurveys.jsx:", err); }
  }, []);
  useEffect(() => { fetch(); if (isManagement) fetchUsers(); }, []);

  const handleSubmit = async () => {
    if (!form.reason_for_leaving) { toast.error('Reason required'); return; }
    const payload = isManagement && form.employee_id ? form : { ...form, employee_id: user.id, employee_name: user.name };
    try { await axios.post(`${API}/exit/surveys`, payload, { headers: getHeaders() }); toast.success('Survey submitted'); setShowForm(false); fetch(); } catch (e) { toast.error('Failed'); }
  };

  const stars = (rating) => Array.from({ length: 5 }, (_, i) => (
    <Star key={`star-${i}`} size={14} className={i < rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'} />
  ));

  return (
    <div data-testid="exit-surveys-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}><MessageSquare className="inline-block mr-2 mb-1" size={32} />Exit Surveys</h1>
        <p className="text-slate-500 text-sm">Capture exit feedback and insights from departing employees</p>
      </div>
      <div className="flex justify-end mb-4"><Button data-testid="new-survey-btn" onClick={() => setShowForm(true)} className="bg-blue-700 hover:bg-blue-800 text-white rounded-lg text-xs"><Plus size={14} className="mr-1" />New Survey</Button></div>
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg"><DialogHeader><DialogTitle>Exit Survey</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {isManagement && (
              <div><Label className="text-xs font-bold">Employee</Label><select data-testid="survey-employee" value={form.employee_id} onChange={e => { const u = allUsers.find(x => x.id === e.target.value); setForm({...form, employee_id: e.target.value, employee_name: u?.name || ''}); }} className="flex h-9 w-full rounded-lg border border-slate-200 bg-white px-3 py-1 text-sm sr-only"><option value="">Self</option>{allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select>
                <div><SearchableSelect value={form.employee_id} onChange={(id) => { const u = allUsers.find(x => x.id === id); setForm({...form, employee_id: id, employee_name: u?.name || ''}); }} items={allUsers} emptyLabel="Self" placeholder="Search employee…" testId="survey-employee-search" allowClear noneLabel="— Self —" /></div></div>
            )}
            <div>
              <Label className="text-xs font-bold">Overall Rating</Label>
              <div className="flex gap-1 mt-1">{[1,2,3,4,5].map(r => (
                <button key={r} data-testid={`rating-${r}`} onClick={() => setForm({...form, overall_rating: r})} className="p-1">
                  <Star size={24} className={r <= form.overall_rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'} />
                </button>
              ))}</div>
            </div>
            <div><Label className="text-xs font-bold">Reason for Leaving</Label><Textarea data-testid="survey-reason" value={form.reason_for_leaving} onChange={e => setForm({...form, reason_for_leaving: e.target.value})} /></div>
            <div><Label className="text-xs font-bold">What did you like most?</Label><Textarea value={form.what_liked} onChange={e => setForm({...form, what_liked: e.target.value})} /></div>
            <div><Label className="text-xs font-bold">What could be improved?</Label><Textarea value={form.what_could_improve} onChange={e => setForm({...form, what_could_improve: e.target.value})} /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.would_recommend} onChange={e => setForm({...form, would_recommend: e.target.checked})} /> Would recommend this company to others</label>
            <div><Label className="text-xs font-bold">Additional Comments</Label><Textarea value={form.additional_comments} onChange={e => setForm({...form, additional_comments: e.target.value})} /></div>
            <Button data-testid="submit-survey-btn" onClick={handleSubmit} className="w-full bg-blue-700 hover:bg-blue-800 text-white">Submit Survey</Button>
          </div>
        </DialogContent>
      </Dialog>
      <div className="space-y-3">
        {surveys.length === 0 ? <p className="text-center text-slate-400 py-12">No exit surveys yet</p> : surveys.map((s, i) => (
          <div key={s.id || i} data-testid={`survey-${i}`} className="bg-white border border-blue-100 rounded-lg shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-bold text-blue-900">{s.employee_name}</p>
                <p className="text-xs text-slate-500">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</p>
              </div>
              <div className="flex items-center gap-1">{stars(s.overall_rating)} <span className="text-xs font-bold text-slate-600 ml-1">{s.overall_rating}/5</span></div>
            </div>
            <div className="space-y-2 text-xs">
              <div><span className="font-bold text-blue-900">Reason:</span> <span className="text-slate-600">{s.reason_for_leaving}</span></div>
              {s.what_liked && <div><span className="font-bold text-emerald-700">Liked:</span> <span className="text-slate-600">{s.what_liked}</span></div>}
              {s.what_could_improve && <div><span className="font-bold text-amber-700">Improve:</span> <span className="text-slate-600">{s.what_could_improve}</span></div>}
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${s.would_recommend ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>{s.would_recommend ? 'Would Recommend' : 'Would Not Recommend'}</span>
              </div>
              {s.additional_comments && <div className="text-slate-500 italic">{s.additional_comments}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
